import { z } from 'zod';
import { apiClient } from '@api/client';
import type { CommonResponse } from '@/types/api';
import {
  stagingRowSchema,
  type ListStagingParams,
  type StagingRow,
} from './schema';

const BASE_PATH = '/observation-staging';

const paginationSchema = z
  .object({
    page: z.number(),
    pageSize: z.number(),
    totalRecords: z.number(),
    isLastPage: z.boolean(),
  })
  .optional();

const listResponseSchema = z.object({
  message: z.string(),
  columnDefs: z.array(z.record(z.unknown())).optional(),
  rowData: z.array(stagingRowSchema),
  gridOptions: z.record(z.unknown()).optional(),
  paginationDetails: paginationSchema,
});

const recordResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: stagingRowSchema.nullable(),
  error: z.string().nullable().optional(),
});

// Approve body — every field optional; a supplied field is a reviewer edit.
export interface ApproveStagingPayload {
  priceSeriesId?: string;
  priceDate?: string;
  minPrice?: number;
  modalPrice?: number;
  maxPrice?: number;
  commodityArrivals?: number;
  commodityTraded?: number;
}

export interface RejectStagingPayload {
  rejectReason: string;
}

function pruneParams(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

export async function listObservationStaging(
  params: ListStagingParams,
): Promise<CommonResponse<StagingRow>> {
  const response = await apiClient.get(BASE_PATH, {
    params: pruneParams(params as unknown as Record<string, unknown>),
  });
  const parsed = listResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(`Invalid list response: ${parsed.error.message}`);
  }
  return parsed.data;
}

function parseRecord(data: unknown): StagingRow {
  const parsed = recordResponseSchema.safeParse(data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function getObservationStaging(id: string): Promise<StagingRow> {
  const response = await apiClient.get(`${BASE_PATH}/${id}`);
  return parseRecord(response.data);
}

export async function approveObservation(
  id: string,
  payload: ApproveStagingPayload,
): Promise<StagingRow> {
  const response = await apiClient.post(`${BASE_PATH}/${id}/approve`, payload, {
    // Resource-state idempotency on the server; the key makes a retried
    // double-click a safe no-op that returns the already-promoted row.
    headers: { 'Idempotency-Key': `approve-${id}` },
  });
  return parseRecord(response.data);
}

export async function rejectObservation(
  id: string,
  payload: RejectStagingPayload,
): Promise<StagingRow> {
  const response = await apiClient.post(`${BASE_PATH}/${id}/reject`, payload);
  return parseRecord(response.data);
}
